<template>
  <div
    :class="
      embedded
        ? 'sidebar__workspace-block'
        : 'sidebar__section sidebar__section--scrollable'
    "
  >
    <div
      v-if="hasItems"
      :class="
        embedded
          ? 'sidebar__workspace-block-inner'
          : 'sidebar__section-scrollable'
      "
    >
      <div
        :class="
          embedded
            ? 'sidebar__workspace-block-content'
            : 'sidebar__section-scrollable-inner'
        "
        data-highlight="applications"
      >
        <ul v-if="pendingJobs[null].length" class="tree">
          <component
            :is="getPendingJobComponent(job)"
            v-for="job in pendingJobs[null]"
            :key="job.id"
            :job="job"
          >
          </component>
        </ul>
        <ul class="tree sidebar__workspace-resource-groups">
          <SidebarNavGroup
            v-if="calendarEntries.length"
            :title="$t('solutionKits.calendar.heading')"
            icon="iconoir-calendar"
            :count="calendarEntries.length"
          >
            <ul class="tree__subs" data-highlight="applications">
              <li
                v-for="entry in calendarEntries"
                :key="entry.id"
                class="tree__sub"
              >
                <a class="tree__sub-link" @click.prevent="openCalendar(entry)">
                  <span class="tree__sub-name">{{ entry.name }}</span>
                </a>
              </li>
            </ul>
          </SidebarNavGroup>

          <SidebarNavGroup
            v-if="kanbanEntries.length"
            :title="$t('solutionKits.kanban.heading')"
            icon="iconoir-kanban-board"
            :count="kanbanEntries.length"
          >
            <ul class="tree__subs" data-highlight="applications">
              <li
                v-for="entry in kanbanEntries"
                :key="entry.id"
                class="tree__sub"
              >
                <a class="tree__sub-link" @click.prevent="openKanban(entry)">
                  <span class="tree__sub-name">{{ entry.name }}</span>
                </a>
              </li>
            </ul>
          </SidebarNavGroup>

          <SidebarNavGroup
            v-if="timelineEntries.length"
            :title="$t('solutionKits.timeline.heading')"
            icon="iconoir-clock"
            :count="timelineEntries.length"
          >
            <ul class="tree__subs" data-highlight="applications">
              <li
                v-for="entry in timelineEntries"
                :key="entry.id"
                class="tree__sub"
              >
                <a class="tree__sub-link" @click.prevent="openTimeline(entry)">
                  <span class="tree__sub-name">{{ entry.name }}</span>
                </a>
              </li>
            </ul>
          </SidebarNavGroup>

          <SidebarNavGroup
            v-for="applicationGroup in visibleApplicationGroups"
            :key="applicationGroup.type"
            :title="applicationGroup.name"
            :icon="applicationGroupIcon(applicationGroup.type)"
            :count="applicationGroup.applications.length"
          >
            <ul
              class="tree"
              :class="{
                'margin-bottom-0': pendingJobs[applicationGroup.type].length,
              }"
              :data-highlight="`applications-${applicationGroup.type}`"
            >
              <component
                :is="getApplicationComponent(application)"
                v-for="application in applicationGroup.applications"
                :key="application.id"
                v-sortable="{
                  id: application.id,
                  update: orderApplications,
                  handle: '[data-sortable-handle]',
                  marginTop: -1.5,
                  enabled: $hasPermission(
                    'workspace.order_applications',
                    selectedWorkspace,
                    selectedWorkspace.id
                  ),
                }"
                :application="application"
                :pending-jobs="pendingJobs[application.type]"
                :workspace="selectedWorkspace"
              ></component>
            </ul>
            <ul v-if="pendingJobs[applicationGroup.type].length" class="tree">
              <component
                :is="getPendingJobComponent(job)"
                v-for="job in pendingJobs[applicationGroup.type]"
                :key="job.id"
                :job="job"
              ></component>
            </ul>
          </SidebarNavGroup>
        </ul>
      </div>
    </div>
    <div
      v-if="
        $hasPermission(
          'workspace.create_application',
          selectedWorkspace,
          selectedWorkspace.id
        )
      "
      class="sidebar__new-wrapper"
      :class="{
        'sidebar__new-wrapper--separator': hasItems,
      }"
      data-highlight="create-new"
    >
      <a
        ref="createApplicationContextLink"
        class="sidebar__new"
        @click="
          $refs.createApplicationContext.toggle(
            $refs.createApplicationContextLink
          )
        "
      >
        <i class="sidebar__new-icon iconoir-plus"></i>
        {{ $t('action.createNew') }}...
      </a>
    </div>
    <CreateApplicationContext
      ref="createApplicationContext"
      :workspace="selectedWorkspace"
    ></CreateApplicationContext>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { notifyIf } from '@baserow/modules/core/utils/error'
import CreateApplicationContext from '@baserow/modules/core/components/application/CreateApplicationContext'
import SidebarNavGroup from '@baserow/modules/core/components/sidebar/SidebarNavGroup'

export default {
  name: 'SidebarWithWorkspace',
  components: { CreateApplicationContext, SidebarNavGroup },
  props: {
    applications: {
      type: Array,
      required: true,
    },
    selectedWorkspace: {
      type: Object,
      required: true,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    /**
     * Because all the applications that belong to the user are in the store we will
     * filter on the selected workspace here.
     */
    groupedApplicationsForSelectedWorkspace() {
      const applicationTypes = Object.values(
        this.$registry.getAll('application')
      ).map((applicationType) => {
        return {
          name: applicationType.getNamePlural(),
          type: applicationType.getType(),
          developmentStage: applicationType.developmentStage,
          applications: this.applications
            .filter((application) => {
              return (
                application.workspace.id === this.selectedWorkspace.id &&
                application.type === applicationType.getType() &&
                applicationType.isVisible(application) &&
                this.$canNavCapability(
                  `database_app:${application.id}`,
                  this.selectedWorkspace.id
                )
              )
            })
            .sort((a, b) => a.order - b.order),
        }
      })
      return applicationTypes
    },
    visibleApplicationGroups() {
      return this.groupedApplicationsForSelectedWorkspace.filter(
        (group) => group.applications.length > 0
      )
    },
    applicationsCount() {
      return this.groupedApplicationsForSelectedWorkspace.reduce(
        (acc, group) => acc + group.applications.length,
        0
      )
    },
    pendingJobs() {
      const grouped = { null: [] }
      Object.values(this.$registry.getAll('application')).forEach(
        (applicationType) => {
          grouped[applicationType.getType()] = []
        }
      )
      this.$store.getters['job/getAll'].forEach((job) => {
        const jobType = this.$registry.get('job', job.type)
        if (jobType.isJobPartOfWorkspace(job, this.selectedWorkspace)) {
          grouped[jobType.getSidebarApplicationTypeLocation(job)].push(job)
        }
      })
      return grouped
    },
    ...mapGetters({
      installedKitsForWorkspace: 'solutionKit/getInstalledKitsForWorkspace',
    }),
    kanbanEntries() {
      if (!this.selectedWorkspace || !this.installedKitsForWorkspace) {
        return []
      }
      const workspaceId = this.selectedWorkspace.id
      const installations = this.installedKitsForWorkspace(workspaceId) || []
      const entries = installations
        .filter(
          (installation) =>
            installation &&
            installation.kit &&
            installation.status === 'succeeded'
        )
        .flatMap((installation) => {
          const summary = installation.summary || {}
          const applications = summary.applications || []
          const entries = []

          applications.forEach((app) => {
            const views = app.views || []
            views.forEach((view) => {
              if (!view || view.logical_type !== 'kanban') {
                return
              }
              entries.push({
                id: `${installation.id}:${view.id}`,
                installationId: installation.id,
                viewId: view.id,
                tableId: view.table_id,
                name:
                  view.name ||
                  this.$t('solutionKits.kanban.salesPipelineLabel') ||
                  `Kanban ${installation.kit.name}`,
              })
            })
          })
          return entries
        })
        .filter((entry) =>
          this.$canNavCapability(
            `table_view_kanban:${entry.tableId}`,
            workspaceId
          )
        )
      return entries
    },
    calendarEntries() {
      if (!this.selectedWorkspace || !this.installedKitsForWorkspace) {
        return []
      }
      const workspaceId = this.selectedWorkspace.id
      const installations = this.installedKitsForWorkspace(workspaceId) || []
      const entries = installations
        .filter(
          (installation) =>
            installation &&
            installation.kit &&
            installation.status === 'succeeded'
        )
        .flatMap((installation) => {
          const summary = installation.summary || {}
          const applications = summary.applications || []
          const entries = []

          applications.forEach((app) => {
            const views = app.views || []
            views.forEach((view) => {
              if (!view || view.logical_type !== 'calendar') {
                return
              }
              entries.push({
                id: `${installation.id}:${view.id}`,
                installationId: installation.id,
                viewId: view.id,
                tableId: view.table_id,
                name:
                  view.name ||
                  this.$t('solutionKits.calendar.heading') ||
                  `Calendar ${installation.kit.name}`,
              })
            })
          })

          return entries
        })
        .filter((entry) =>
          this.$canNavCapability(
            `table_view_calendar:${entry.tableId}`,
            workspaceId
          )
        )
      return entries
    },
    timelineEntries() {
      if (!this.selectedWorkspace || !this.installedKitsForWorkspace) {
        return []
      }
      const workspaceId = this.selectedWorkspace.id
      const installations = this.installedKitsForWorkspace(workspaceId) || []
      const entries = installations
        .filter(
          (installation) =>
            installation &&
            installation.kit &&
            installation.status === 'succeeded'
        )
        .flatMap((installation) => {
          const summary = installation.summary || {}
          const applications = summary.applications || []
          const entries = []

          applications.forEach((app) => {
            const views = app.views || []
            views.forEach((view) => {
              if (!view || view.logical_type !== 'timeline') {
                return
              }
              entries.push({
                id: `${installation.id}:${view.id}`,
                installationId: installation.id,
                viewId: view.id,
                name:
                  view.name ||
                  this.$t('solutionKits.timeline.heading') ||
                  `Timeline ${installation.kit.name}`,
              })
            })
          })

          return entries
        })
      return entries
    },
    hasItems() {
      return (
        this.applicationsCount ||
        this.pendingJobs.null.length ||
        this.kanbanEntries.length ||
        this.calendarEntries.length ||
        this.timelineEntries.length
      )
    },
  },
  watch: {
    selectedWorkspace: {
      immediate: false,
      handler(newWorkspace, oldWorkspace) {
        if (!newWorkspace) {
          return
        }
        if (!oldWorkspace || newWorkspace.id !== oldWorkspace.id) {
          this.fetchKanbanInstallations()
        }
      },
    },
  },
  mounted() {
    this.fetchKanbanInstallations()
  },
  methods: {
    getApplicationComponent(application) {
      return this.$registry
        .get('application', application.type)
        .getSidebarComponent()
    },
    getPendingJobComponent(job) {
      return this.$registry.get('job', job.type).getSidebarComponent()
    },
    applicationGroupIcon(type) {
      const icons = {
        database: 'iconoir-db',
        automation: 'baserow-icon-automation',
        builder: 'iconoir-window',
      }
      return icons[type] || 'iconoir-folder'
    },
    async orderApplications(order, oldOrder) {
      try {
        await this.$store.dispatch('application/order', {
          workspace: this.selectedWorkspace,
          order,
          oldOrder,
        })
      } catch (error) {
        notifyIf(error, 'application')
      }
    },
    async fetchKanbanInstallations() {
      if (!this.selectedWorkspace) {
        return
      }
      try {
        await this.$store.dispatch(
          'solutionKit/fetchWorkspaceKits',
          this.selectedWorkspace.id
        )
      } catch (error) {
        notifyIf(error, 'solutionKit')
      }
    },
    openKanban(entry) {
      this.$router.push({
        name: 'solution-kit-kanban',
        query: {
          installationId: entry.installationId || entry.id,
          viewId: entry.viewId || 'kanban_pipeline',
        },
      })
    },
    openCalendar(entry) {
      this.$router.push({
        name: 'solution-kit-calendar',
        query: {
          installationId: entry.installationId || entry.id,
          viewId: entry.viewId,
        },
      })
    },
    openTimeline(entry) {
      this.$router.push({
        name: 'solution-kit-timeline',
        query: {
          installationId: entry.installationId || entry.id,
          viewId: entry.viewId,
        },
      })
    },
  },
}
</script>
